import { useCallback, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnalyticsEvents, sectionStore, userStore } from "@eolas-medical/core";
import envConfig from "env-config";
import { useGetAllAccessRequests } from "Hooks/usersService";
import { InnerPageWrapper, PageTitle, NavButton, Modal } from "UIKit";
import {
  UserList,
  LoginTokens,
  TrustedEmails,
  ManageAdminsModal,
  UserBreakdownModal,
  AccessRequestsModal,
} from "./components";
import { trackEvent } from "API/Analytics";
import { ManageUsersButtons } from "./components/ManageUserButtons/ManageUsersButtons";
import InviteUsersModal from "./components/InviteUser/InviteUsersModal/InviteUsersModal";
import InviteUsersByEmailModal from "./components/InviteUser/InviteUsersByEmailModal/InviteUsersByEmailModal";

import useGenerateAccessToken from "modules/spaces/data/useGenerateAccessToken";
import AccessQRCodeModal from "UIKit/Modal/AccessQRCodeModal/AccessQRCodeModal";
import AccessType from "./components/AccessType/AccessType";

import { AccessLinkToken } from "Pages/Authentication/pages/Invitations/pages/AccessLink/AccessLink";
import useSpacesContext from "modules/spaces/context/useSpacesContext";
import useSpaceUsers from "modules/spaces/data/useSpaceUsers";
import useGetAccessToken from "modules/spaces/data/useGetAccessToken";
import { useEolasNavigation } from "Components/Navigation/hooks";

export type ManageUsersModalState =
  | "InviteUsers"
  | "AccessRequests"
  | "UserBreakdown"
  | "ManageAdmins"
  | "InviteByEmail"
  | "InviteByLink";

const ManageUsers = () => {
  const { url } = useRouteMatch();
  const { t } = useTranslation();
  const { count: accessRequestsCount } = useGetAllAccessRequests();
  const [modalState, setModalState] = useState<ManageUsersModalState | null>(null);
  const { userID: userId = "" } = userStore;
  const { selectedSpace, isPublic } = useSpacesContext();
  const { activeTab } = useEolasNavigation();
  const { spaceUsers, refetchUserList } = useSpaceUsers(sectionStore.appID);

  const { accessToken, gettingAccessToken } = useGetAccessToken(selectedSpace?.id as string);

  const { generateAccessToken, generatingAccessToken } = useGenerateAccessToken(
    selectedSpace?.id as string,
  );

  const inviteUsersByLinkRoute = `${envConfig.REACT_APP_PROTOCOL}://${envConfig.REACT_APP_WEB_DOMAIN}/access-link/${accessToken}`;

  const handleCopyLink = useCallback(() => {
    trackEvent(AnalyticsEvents.ACCESS_LINK_COPIED, { userId });
  }, [userId]);

  const handleExportToImage = useCallback(() => {
    trackEvent(AnalyticsEvents.ACCESS_QR_EXPORTED, { userId });
  }, [userId]);

  const canChangeAccessType = activeTab === "spaces";

  return (
    <>
      <InnerPageWrapper>
        <PageTitle title={t("manageUsers_pageTitle")} />
        <NavButton to={url.replace("/manageUsers", "")} />

        <ManageUsersButtons
          accessRequestsCount={accessRequestsCount}
          setModalState={setModalState}
        />

        {!isPublic && <LoginTokens />}

        {canChangeAccessType && <AccessType />}

        {!isPublic && <TrustedEmails />}

        <UserList users={spaceUsers} getUsers={refetchUserList} />
      </InnerPageWrapper>
      <Modal open={!!modalState} onClose={() => setModalState(null)}>
        {modalState === "UserBreakdown" && <UserBreakdownModal users={spaceUsers} />}

        {modalState === "ManageAdmins" && (
          <ManageAdminsModal appUsers={spaceUsers} refetchUserList={refetchUserList} />
        )}

        {modalState === "AccessRequests" && <AccessRequestsModal refetchUsers={refetchUserList} />}

        {modalState === "InviteUsers" && (
          <InviteUsersModal
            isPublic={isPublic}
            onClickViaLink={() => setModalState("InviteByLink")}
            onClickViaEmail={() => setModalState("InviteByEmail")}
          />
        )}

        {modalState === "InviteByEmail" && <InviteUsersByEmailModal />}

        {modalState === "InviteByLink" && (
          <AccessQRCodeModal
            titleText={t("manageUsers_invite_by_link_title")}
            descriptionText={t("manageUsers_invite_by_link_subtitle")}
            onDone={() => setModalState(null)}
            onGenerateAccessToken={generateAccessToken}
            accessToken={accessToken as AccessLinkToken}
            onCopyLink={handleCopyLink}
            onExportToImage={handleExportToImage}
            isLoading={gettingAccessToken || generatingAccessToken}
            accessRoute={inviteUsersByLinkRoute}
            showRegenerate
          />
        )}
      </Modal>
    </>
  );
};

export default ManageUsers;
